// Settings-01 / Settings-01b — Team Members table.
//
// Columns: Name, Email, Title, Producer, Status, Last Active, Actions.
// Active members get an Edit pencil; pending invites get Resend +
// Revoke buttons. Status renders as a badge; Producer shows "Yes" /
// dash so the column is scannable. ``Last Active`` derives from the
// most recent users_session row (login + future refresh both create
// fresh rows). Pending rows show a dash.

import { Pencil, RotateCcw, X } from "lucide-react";

import {
  Badge, Button, Table, THead, TBody, Tr, Th, Td,
} from "@/design-system";

import {
  invitationIdFor,
  isPendingInvitation,
  type TeamMember,
  type TeamMemberStatus,
} from "./types";


function statusVariant(
  status: TeamMemberStatus,
): "neutral" | "category" | "regulatory" {
  switch (status) {
    case "active":
      return "category";
    case "invited":
      return "neutral";
    case "suspended":
      return "regulatory";
    case "deactivated":
      return "regulatory";
  }
}


function statusLabel(status: TeamMemberStatus): string {
  switch (status) {
    case "active":
      return "Active";
    case "invited":
      return "Invited";
    case "suspended":
      return "Suspended";
    case "deactivated":
      return "Deactivated";
  }
}


function fullName(row: TeamMember): string {
  const joined = `${row.first_name} ${row.last_name}`.trim();
  return joined || row.email;
}


function roleLabel(role: string): string {
  switch (role) {
    case "owner": return "Owner";
    case "admin": return "Admin";
    case "member": return "Member";
    case "sales": return "Sales";
    default: return role;
  }
}


/** Render ``last_active_at`` as a human-friendly relative time.
 *
 * The signal is intentionally coarse — pending invites have ``null``
 * (rendered as a dash), members who have never logged in show
 * "Never," and otherwise we collapse to "just now / Xm / Xh /
 * Yesterday / Xd / Xw / formatted date" buckets. ``now`` is
 * injected so unit tests can pin the boundaries deterministically.
 */
export function formatLastActive(
  iso: string | null,
  status: TeamMemberStatus,
  now: Date = new Date(),
): string {
  if (status === "invited") return "—";
  if (!iso) return "Never";
  const then = new Date(iso);
  const diffMs = now.getTime() - then.getTime();
  if (diffMs < 0) return "Just now";
  const minute = 60_000;
  const hour = 60 * minute;
  const day = 24 * hour;
  if (diffMs < 5 * minute) return "Just now";
  if (diffMs < hour) return `${Math.floor(diffMs / minute)}m ago`;
  if (diffMs < day) return `${Math.floor(diffMs / hour)}h ago`;
  if (diffMs < 2 * day) return "Yesterday";
  if (diffMs < 7 * day) return `${Math.floor(diffMs / day)}d ago`;
  if (diffMs < 30 * day) {
    return `${Math.floor(diffMs / (7 * day))}w ago`;
  }
  // Longer than a month: the table cares less about precision; fall
  // back to a localized short date so the column stays scannable.
  return then.toLocaleDateString(undefined, {
    month: "short", day: "numeric", year: "numeric",
  });
}


export interface TeamMembersTableProps {
  rows: TeamMember[];
  onEdit: (row: TeamMember) => void;
  onResend: (row: TeamMember) => void;
  onRevoke: (row: TeamMember) => void;
  resendingMemberId?: string | null;
}


export function TeamMembersTable({
  rows, onEdit, onResend, onRevoke, resendingMemberId = null,
}: TeamMembersTableProps) {
  return (
    <div
      data-testid="team-members-table-wrap"
      className="overflow-x-auto rounded-lg border border-border-subtle"
    >
      <Table
        scroll={false}
        data-testid="team-members-table"
        className="border-collapse"
      >
        <THead className="border-b border-border-subtle bg-recessed text-xs font-medium uppercase tracking-wide text-text-tertiary">
          <Tr>
            <Th className="px-4">Name</Th>
            <Th className="px-4">Email</Th>
            <Th className="px-4">Title</Th>
            <Th className="px-4">Roles</Th>
            <Th className="px-4">Producer</Th>
            <Th className="px-4">Status</Th>
            <Th className="px-4">Last Active</Th>
            <Th className="px-4 text-right">Actions</Th>
          </Tr>
        </THead>
        <TBody>
          {rows.map((row) => {
            const pending = isPendingInvitation(row);
            const invitationId = invitationIdFor(row);
            const isResending =
              invitationId !== null
              && resendingMemberId === row.member_id;
            return (
              <Tr
                key={row.member_id}
                data-testid={`team-member-row-${row.member_id}`}
                className="border-b border-border-subtle last:border-b-0"
              >
                <Td className="px-4 py-3 text-text-primary">
                  {fullName(row)}
                </Td>
                <Td className="px-4 py-3 text-text-secondary">
                  {row.email}
                </Td>
                <Td
                  data-testid={`team-member-title-${row.member_id}`}
                  className="px-4 py-3 text-text-secondary"
                >
                  {row.title || "—"}
                </Td>
                <Td
                  data-testid={`team-member-roles-${row.member_id}`}
                  className="px-4 py-3 text-text-secondary"
                >
                  {row.roles && row.roles.length > 0
                    ? row.roles.map(roleLabel).join(", ")
                    : "—"}
                </Td>
                <Td
                  data-testid={`team-member-producer-${row.member_id}`}
                  className="px-4 py-3 text-text-secondary"
                >
                  {row.is_producer ? "Yes" : "—"}
                </Td>
                <Td className="px-4 py-3">
                  <Badge
                    data-testid={`team-member-status-${row.member_id}`}
                    variant={statusVariant(row.status)}
                  >
                    {statusLabel(row.status)}
                  </Badge>
                </Td>
                <Td
                  data-testid={`team-member-last-active-${row.member_id}`}
                  className="px-4 py-3 text-text-secondary"
                >
                  {formatLastActive(row.last_active_at, row.status)}
                </Td>
                <Td className="px-4 py-3 text-right">
                  {pending ? (
                    <div className="inline-flex items-center justify-end gap-1">
                      <Button
                        variant="tertiary"
                        size="sm"
                        loading={isResending}
                        data-testid={`team-member-resend-${row.member_id}`}
                        onClick={() => onResend(row)}
                      >
                        <RotateCcw size={14} aria-hidden="true" />
                        <span>Resend</span>
                      </Button>
                      <Button
                        variant="tertiary"
                        size="sm"
                        data-testid={`team-member-revoke-${row.member_id}`}
                        onClick={() => onRevoke(row)}
                      >
                        <X size={14} aria-hidden="true" />
                        <span>Revoke</span>
                      </Button>
                    </div>
                  ) : (
                    <Button
                      variant="tertiary"
                      size="sm"
                      data-testid={`team-member-edit-${row.member_id}`}
                      onClick={() => onEdit(row)}
                    >
                      <Pencil size={14} aria-hidden="true" />
                      <span>Edit</span>
                    </Button>
                  )}
                </Td>
              </Tr>
            );
          })}
        </TBody>
      </Table>
    </div>
  );
}
